import {
  authGetMyStoreLinkToken,
  authVendorGuest,
  contractDetails,
  contractGetNotes,
  contractSendRequoteEmailToConsumer,
  contractSendTradeCustomerQuoteReadyEmail,
  contractUpdateConsumer,
  contractUpdateJobRequote,
  contractUpdateNote,
  customerGet,
  customerGetAllExtraItems,
  customerGetAllProductsWithOverrides,
  customerGetCompanySettings,
  customerGetFinancialGroups,
  customerGetProcessingLevels,
  customerGetProductsWithDefaults,
  customerUpdateCompanySettings,
  customerUpdateExtraItemGroups,
  customerUpdateExtraItemOptions,
  customerUpdateExtraItems,
  customerUpdateProductOverrides,
  getProducts,
  processingDeleteJob,
  processingGetAvailableProductSelectionGroups,
  processingGetAvailableProductSelectionTypes,
  processingGetAvailableProductsToSelect,
  processingGetDocsToAccept,
  processingGetEnquiryStatuses,
  processingProductSelectionStage,
  processingProductStoredDesigns,
  processingReloadContract,
  processingRemoveContract,
  processingSaveContract,
  processingUpdateDealerEnquiryStatus,
  processingUpdateEnquiryStatus,
  processingUpdateQuoteStatus,
  setupGetDatasets,
  setupUpdateProducts,
  staffAssignEnquiry,
  staffGetFinancialGroups,
  staffGetLatestExchangeRates,
  staffGetThisListAnomaly,
  staffListCustomers,
  staffListDiscountCategories,
  staffUpdateAlternativePriceData,
  staffUpdateCurrency,
  staffUpdateOrganisationDetails,
  staffUpdateProductSelectionGroups,
  staffUpdateProductSelectionTypes,
  staffUpdateThisListAnomaly,
  vendorGetThisGridAnomaly,
  vendorUpdateThisGridAnomaly
} from '@/api'
import { ContactDepartments, NoteType } from '@/enums'
import { useAuthStore } from '@/pinia/auth'
import { useBasketStore } from '@/pinia/basket'
import { useBrandedStore } from '@/pinia/branded'
import { useCustomerStore } from '@/pinia/customer'
import { addMinutesToDate, isValidDate } from '@/utils/date'
import { defineStore } from 'pinia'

export const useTouchStore = defineStore('touch', {
  persist: {
    omit: ['pageProductStoredDesignsCache']
  },
  state: () => ({
    processingLevels: [],
    pageProductStoredDesignsCache: {
      cachedPages: {},
      cacheExpiry: undefined,
      cacheLifetimeMins: 30
    },
    isDealerNetworkPartnerOnly: false
  }),
  actions: {
    setDealerNetworkPartnerOnly(isDealerNetworkPartnerOnly) {
      this.isDealerNetworkPartnerOnly = isDealerNetworkPartnerOnly
    },
    SET_PROCESSING_LEVELS(processingLevels) {
      this.processingLevels = processingLevels
    },
    ADD_TO_PAGE_STORED_DESIGNS_CACHE({ itemKey, item }) {
      if (!Object.keys(this.pageProductStoredDesignsCache.cachedPages).length) {
        // if there are no pages cached lets set a new cache expiry time
        this.pageProductStoredDesignsCache.cacheExpiry = addMinutesToDate(
          new Date(),
          this.pageProductStoredDesignsCache.cacheLifetimeMins
        )
      }
      this.pageProductStoredDesignsCache.cachedPages[itemKey] = item
    },
    CLEAR_STORED_DESIGNS_CACHE() {
      this.pageProductStoredDesignsCache.cachedPages = {}
    },
    customerGetCompanySettings() {
      return customerGetCompanySettings()
    },
    customerUpdateCompanySettings({ showSizes, showDesignNames }) {
      return customerUpdateCompanySettings(showSizes, showDesignNames)
    },
    sendTradeCustomerQuoteReadyEmail({ contractId, jobKey }) {
      return contractSendTradeCustomerQuoteReadyEmail(contractId, jobKey)
    },
    staffListDiscountCategories() {
      return staffListDiscountCategories()
    },
    async loadProcessingLevels() {
      if (this.processingLevels.length === 0) {
        this.SET_PROCESSING_LEVELS(await customerGetProcessingLevels())
      }
    },
    getDatasetIds() {
      return setupGetDatasets()
    },
    customerGetProductsWithDefaults(datasetId) {
      return customerGetProductsWithDefaults(datasetId)
    },
    staffUpdateAlternativePriceData({ CustomerId, DatasetId, Anomalies, customerName }) {
      return staffUpdateAlternativePriceData(CustomerId, DatasetId, Anomalies, customerName)
    },
    staffGetFinancialGroups({ CustomerId, DatasetId }) {
      return staffGetFinancialGroups(CustomerId, DatasetId)
    },
    async staffGetLatestExchangeRates() {
      return staffGetLatestExchangeRates()
    },
    async staffUpdateCurrency(param) {
      return staffUpdateCurrency(param)
    },
    async processingGetAvailableProductsToSelect({
      typeId,
      groupId,
      sandbox,
      customerId = useBasketStore().customerId
    }) {
      return processingGetAvailableProductsToSelect(typeId, groupId, customerId, sandbox)
    },
    async processingGetAvailableProductSelectionGroups(customerId) {
      return processingGetAvailableProductSelectionGroups(customerId)
    },
    async processingGetAvailableProductSelectionTypes({ customerId, groupId }) {
      return processingGetAvailableProductSelectionTypes(customerId, groupId)
    },
    async saveProductGroup({ title, description, ImageBlob }) {
      return staffUpdateProductSelectionGroups([{ title, description, ImageBlob }])
    },
    async updateProductGroup({ id, title, description, ImageBlob, listOrder }) {
      return staffUpdateProductSelectionGroups([
        {
          id,
          title,
          description,
          ImageBlob,
          listOrder
        }
      ])
    },
    async updateProductType({ id, title, description, ImageBlob, listOrder }) {
      return staffUpdateProductSelectionTypes([
        {
          id,
          title,
          description,
          ImageBlob,
          listOrder
        }
      ])
    },
    async saveProductType({ title, description, ImageBlob }) {
      return staffUpdateProductSelectionTypes([{ title, description, ImageBlob }])
    },
    async deleteProductType(groupId) {
      return staffUpdateProductSelectionTypes([{ Id: groupId, Delete: true }])
    },
    async setupUpdateProducts(params) {
      return setupUpdateProducts([params])
    },
    async deleteProductGroup(groupId) {
      return staffUpdateProductSelectionGroups([{ Id: groupId, Delete: true }])
    },
    async AddExtraItem(params) {
      return customerUpdateExtraItems([
        {
          GroupId: params.category,
          Description: params.name,
          UnitOfMeasure: params.unit_of_measure,
          Price: params.price,
          FittingItem: params.isFittingCharge,
          UseSizesFromProduct: params.useProductMeasurements,
          ImageData: params.ImageData
        }
      ])
    },
    async AddExtraItemOption(params) {
      return customerUpdateExtraItemOptions([
        {
          ItemId: params.itemId,
          Description: params.description,
          Value: params.value
        }
      ])
    },
    async UpdateExtraItemOptions(params) {
      return customerUpdateExtraItemOptions(params)
    },
    async UpdateExtraItemOption(params) {
      return customerUpdateExtraItemOptions([
        {
          id: params.id,
          ItemId: params.itemId,
          Description: params.description,
          Price: params.value,
          Delete: params.deleted
        }
      ])
    },
    async updateExtraItem(params) {
      return customerUpdateExtraItems([
        {
          Id: params.id,
          GroupId: params.category,
          Description: params.name,
          UnitOfMeasure: params.unit_of_measure,
          Value: params.price,
          FittingItem: params.isFittingCharge,
          UseSizesFromProduct: params.useProductMeasurements,
          ImageData: params.ImageData
        }
      ])
    },
    async DeleteExtraItem(id) {
      return customerUpdateExtraItems([
        {
          Id: id,
          Delete: true
        }
      ])
    },
    async AddExtraItemGroup(name) {
      return customerUpdateExtraItemGroups([
        {
          Description: name
        }
      ])
    },
    async UpdateExtraItemGroups(params) {
      return customerUpdateExtraItemGroups(params)
    },
    async deleteExtraItemGroup(groupId) {
      return customerUpdateExtraItemGroups([
        {
          Id: groupId,
          Delete: true
        }
      ])
    },
    async customerGetAllExtraItems() {
      return customerGetAllExtraItems()
    },
    async vendorUpdateThisGridAnomaly(params) {
      const customerStore = useCustomerStore()
      return vendorUpdateThisGridAnomaly(
        customerStore.customer.datasetIds[0],
        params.Id,
        params.FinancialGridConfigurationId,
        params.OverrideCells
      )
    },
    async vendorGetThisGridAnomaly(params) {
      const customerStore = useCustomerStore()
      return vendorGetThisGridAnomaly(
        params.id,
        params.customerId,
        params.datasetId ?? customerStore.customer.datasetIds[0]
      )
    },
    async staffUpdateThisListAnomaly(params) {
      const customerStore = useCustomerStore()
      return staffUpdateThisListAnomaly(
        params.DatasetId ?? customerStore.customer?.datasetIds[0],
        params.Id,
        params.FinancialGridConfigurationId,
        params.OverrideCells,
        params.customerId
      )
    },
    async staffGetThisListAnomaly({ id, customerId, datasetId }) {
      return staffGetThisListAnomaly(id, customerId, datasetId)
    },
    async customerGetFinancialGroups() {
      const customerStore = useCustomerStore()
      return customerGetFinancialGroups(customerStore.customer.datasetIds[0])
    },
    async staffUpdateOrganisationDetails(payload) {
      return staffUpdateOrganisationDetails(payload)
    },
    async assignLead({ contractId, customerIds }) {
      return staffAssignEnquiry(contractId, customerIds)
    },
    async loadProductsForCustomer(customerId) {
      return customerGetAllProductsWithOverrides(customerId)
    },
    async updateProductsForCustomer({ customerId, products }) {
      return customerUpdateProductOverrides(products, customerId)
    },
    async getMyStoreLinkToken() {
      return authGetMyStoreLinkToken()
    },
    async productSelectionStage(selectedStages = []) {
      return processingProductSelectionStage(selectedStages)
    },
    async getProducts() {
      return getProducts()
    },
    async saveContract({ contractId }) {
      const basketStore = useBasketStore()
      return processingSaveContract(basketStore.customerId, contractId)
    },
    async loadActiveQuotes(options) {
      return this.loadQuotes(options)
    },
    async staffListCustomers({
      limit,
      offset,
      query,
      contractId,
      dealerNetworkPartnerOnly,
      customerStatus,
      customerLevel
    }) {
      return staffListCustomers(
        limit,
        offset,
        query,
        contractId ? Number(contractId) : undefined,
        dealerNetworkPartnerOnly || false,
        customerStatus,
        undefined,
        undefined,
        customerLevel
      )
    },
    async updateConsumer(params) {
      const { contractId, customerId, consumer } = params
      const basketStore = useBasketStore()
      await contractUpdateConsumer({
        customerId: customerId || basketStore.customerId,
        contractId,
        firstName: consumer.firstName,
        lastName: consumer.lastName,
        email: consumer.email,
        mobile: consumer.mobile,
        addressLine1: consumer.addressLine1,
        addressLine2: consumer.addressLine2,
        addressLine3: consumer.addressLine3,
        addressTown: consumer.addressTown,
        addressCounty: consumer.addressCounty,
        addressCode: consumer.addressCode,
        addressIso: consumer.addressIso
      })
    },
    async unlockQuote({ contractId }) {
      return processingRemoveContract(contractId)
    },
    async loadEnquiryNotes({ contractId, jobId }) {
      return contractGetNotes({
        ContractId: contractId,
        JobKey: jobId,
        NoteType: NoteType.CUSTOMER
      })
    },
    async loadNotes({ contractId, jobId }) {
      const notes = await contractGetNotes({
        ContractId: contractId,
        JobKey: jobId
      })
      notes.sort((a, b) => new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime())
      return notes
    },
    async loadNote({ contractId, jobId, noteType }) {
      try {
        const notes = (
          await contractGetNotes({
            ContractId: contractId,
            JobKey: jobId
          })
        ).filter((note) => note.noteType === noteType)

        if (notes.length === 0) {
          return null
        }

        return notes[0]
      } catch {
        return null
      }
    },
    async updateNote({ contractId, jobId, noteType, noteId, text, deleteNote = false }) {
      return contractUpdateNote({
        ContractId: contractId,
        JobKey: jobId,
        NoteType: noteType,
        NoteId: noteId,
        Text: text,
        Delete: deleteNote
      })
    },
    async createNote({ contractId, jobId, noteType, text }) {
      return contractUpdateNote({
        ContractId: contractId,
        JobKey: jobId,
        NoteType: noteType,
        Text: text
      })
    },
    async loadQuote({ customerId, quoteId, jobId }) {
      return contractDetails(customerId, quoteId, jobId)
    },
    async deleteJob({ contractId, jobId }) {
      const basketStore = useBasketStore()
      await processingReloadContract(basketStore.customerId, contractId)
      await processingDeleteJob(contractId, jobId)
      await processingSaveContract(basketStore.customerId, contractId)
    },
    async loadOrder({ customerId, id }) {
      return contractDetails(customerId, id)
    },
    async loadMyAddresses() {
      const customer = await customerGet()

      return customer.addresses
    },
    async setBrandedContract(contractId) {
      const basketStore = useBasketStore()
      basketStore.setContract({
        contractId,
        jobKey: 1,
        isEnquiry: true,
        contractType: 'Enquiry'
      })
    },
    async launch({ slug, returnUrl }) {
      const auth = await authVendorGuest(slug, returnUrl)
      if (auth) {
        const authStore = useAuthStore()
        const brandedStore = useBrandedStore()
        const basketStore = useBasketStore()

        await authStore.setVendorLogin({
          isDealerNetwork: auth.isDealerMode,
          property: 'branded',
          tokenExpiry: auth.expiresAt,
          refreshAfter: auth.refreshAfter
        })

        basketStore.setContract({
          contractId: auth.contractId,
          jobKey: 1,
          isEnquiry: true,
          contractType: 'Enquiry'
        })

        brandedStore.setSlugAndKey({ slug })

        return auth
      }
    },
    async getEnquiryStatuses() {
      return processingGetEnquiryStatuses()
    },
    async updateQuoteStatus({ contractId, status }) {
      const basketStore = useBasketStore()
      await processingReloadContract(basketStore.customerId, contractId)
      await processingUpdateQuoteStatus(contractId, status)
      await processingSaveContract(basketStore.customerId, contractId)
    },
    async updateEnquiryStatus({ contractId, status }) {
      const basketStore = useBasketStore()
      await processingReloadContract(basketStore.customerId, contractId)
      await processingUpdateEnquiryStatus(contractId, status)
      await processingSaveContract(basketStore.customerId, contractId)
    },
    async updateDealerEnquiryStatus({ dealerNetworkAssignmentId, status, refusalReason }) {
      await processingUpdateDealerEnquiryStatus(dealerNetworkAssignmentId, status, refusalReason)
    },
    async getDocsToAccept() {
      return processingGetDocsToAccept()
    },
    async updateJobRequote({ contractId, jobKey, emailSubject, emailBodyText }) {
      return contractUpdateJobRequote({ contractId, jobKey, emailSubject, emailBodyText })
    },
    async sendRequoteEmailToConsumer({ contractId, jobKey }) {
      await contractSendRequoteEmailToConsumer(contractId, jobKey)
    },

    async getProductStoredDesignsForPage({
      customerId,
      processingLevel,
      productId,
      limit = 50,
      offset = 0,
      squaresQty = undefined
    }) {
      /*
       * this has some primitive caching functionality within it
       * if this approach is copied, I'd suggest we refactor and setup a store specific for caches
       * this would handle cache functionality (clearing, cache expiry, etc)
       */
      const cacheKey = `${customerId}${processingLevel}${productId}${undefined}${undefined}${false}${limit}${offset}${squaresQty}`

      if (
        isValidDate(this.pageProductStoredDesignsCache.cacheExpiry) &&
        this.pageProductStoredDesignsCache.cacheExpiry < Date.now()
      ) {
        // if the cache expiry is in the past then we need to clear out the cache
        this.CLEAR_STORED_DESIGNS_CACHE()
      } else if (Object.keys(this.pageProductStoredDesignsCache.cachedPages).includes(cacheKey)) {
        // we check to see if there is a product page in the cache that matches the page we're after
        return this.pageProductStoredDesignsCache.cachedPages[cacheKey]
      }

      const res = await processingProductStoredDesigns(
        customerId,
        processingLevel,
        productId,
        undefined,
        undefined,
        false,
        limit,
        offset,
        squaresQty
      )
      this.ADD_TO_PAGE_STORED_DESIGNS_CACHE({ itemKey: cacheKey, item: res })
      return res
    }
  },
  getters: {
    contactDepartments() {
      const contactDepartments = Object.keys(ContactDepartments).filter((key) => Number.isNaN(Number(key)))
      const capitalise = (word) => word.charAt(0).toUpperCase() + word.slice(1)
      const contactDepartmentsMapped = contactDepartments.map((department, index) => ({
        id: index,
        name: department.toLowerCase().split('_').map(capitalise).join(' ')
      }))
      return contactDepartmentsMapped
    }
  }
})
